import React, { useState } from 'react';
import { Badge, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { subtleBadgeProps } from '@core/utils/subtleBadge';
import PreviewNameList from '@core/reports/components/PreviewNameList';
import type {
  DashboardChecklistDepartmentItem,
  DashboardReportingChecklistPeriod,
} from '../types/dashboard';
import {
  activitySubmitPath,
  departmentProgressPath,
} from '../utils/dashboardPaths';
import { buildActivityEntrySubmitSearch } from '@features/report/activity-entry/constants/activityEntryQueryParams';
import {
  formatDeadlineCountdown,
  formatPeriodRangeLabel,
  formatReportTagDeadline,
  periodChecklistHasOutstanding,
} from '@features/report/tag/utils/reportTagDeadline';
import {
  DASHBOARD_LAYOUT,
  DASHBOARD_REPORTING_CHECKLIST_ID,
  checklistPeriodAnchorId,
} from '../constants/dashboardLayout';
import { mostUrgentChecklistPeriodId } from '../utils/dashboardData';

interface Props {
  periods: DashboardReportingChecklistPeriod[];
  scope: string;
  scopeId: number;
  /** When false, outstanding templates show status only (no Open → submit). */
  canSaveActivity?: boolean;
  /** Controlled expanded period (set by the page so Deadlines can open a period). */
  expandedId?: number | null;
  onToggle?: (reportTagId: number | null) => void;
}

/** Badge tone for a department row readiness label. */
function readinessBadgeVariant(item: DashboardChecklistDepartmentItem): string {
  if (item.is_approved || item.readiness === 'complete') return 'success';
  if (item.readiness === 'blocked' || (item.children_outstanding ?? 0) > 0) return 'secondary';
  return 'primary';
}

function ChecklistProgressBar({ done, total }: { done: number; total: number }) {
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div
      className="dashboard-checklist-bar"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
    >
      <span className="dashboard-checklist-bar__fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

function urgencyBadgeVariant(
  urgency?: string | null
): 'success' | 'warning' | 'danger' | 'info' | 'secondary' {
  if (urgency === 'danger') return 'danger';
  if (urgency === 'warning') return 'warning';
  if (urgency === 'info') return 'info';
  return 'secondary';
}

function childrenNoun(scope: string): string {
  const upper = scope.toUpperCase();
  if (upper === 'NATION') return 'districts';
  if (upper === 'DISTRICT') return 'churches';
  return 'children';
}

/** Ready / blocked hint for outstanding office department reports. */
export function departmentChecklistReadinessLabel(
  item: DashboardChecklistDepartmentItem,
  scope: string
): string | null {
  if (item.is_approved || item.readiness === 'complete') {
    return null;
  }
  const outstanding = item.children_outstanding ?? 0;
  if (item.readiness === 'blocked' || outstanding > 0) {
    const noun = childrenNoun(scope);
    return `Blocked · ${outstanding} ${noun} outstanding`;
  }
  if (item.readiness === 'ready' || item.readiness == null) {
    return 'Ready to complete';
  }
  return null;
}

const ReportingChecklistPanel: React.FC<Props> = ({
  periods,
  scope,
  scopeId,
  canSaveActivity = true,
  expandedId: controlledExpandedId,
  onToggle,
}) => {
  const [localExpandedId, setLocalExpandedId] = useState<number | null>(() =>
    mostUrgentChecklistPeriodId(periods)
  );
  const isControlled = controlledExpandedId !== undefined;
  const expandedId = isControlled ? controlledExpandedId : localExpandedId;
  const setExpandedId = (id: number | null) => {
    if (!isControlled) setLocalExpandedId(id);
    onToggle?.(id);
  };

  if (!periods.length) return null;

  return (
    <Card
      id={DASHBOARD_REPORTING_CHECKLIST_ID}
      className="ntcog-card dashboard-section-card dashboard-checklist-card"
    >
      <Card.Header>
        <div>My checklist</div>
        <small className="text-muted fw-normal d-block">
          What's left to finish for each open reporting period
        </small>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="dashboard-progress-list">
          {periods.map((period) => {
            const isExpanded = expandedId === period.report_tag_id;
            const templateTotal =
              period.templates.submitted + period.templates.outstanding;
            const deptTotal =
              period.department_reports.approved + period.department_reports.outstanding;
            const periodLabel = formatPeriodRangeLabel(period.since, period.until);
            const deadlineLabel = formatReportTagDeadline(period.deadline);
            const hasOutstanding = periodChecklistHasOutstanding(period);
            const countdown = hasOutstanding
              ? formatDeadlineCountdown(period.deadline)
              : null;
            const badgeLabel = hasOutstanding
              ? countdown ?? (deadlineLabel ? 'Deadline set' : 'Open period')
              : templateTotal + deptTotal > 0
                ? 'Complete'
                : deadlineLabel
                  ? 'Deadline set'
                  : 'Open period';
            const badgeVariant = hasOutstanding
              ? urgencyBadgeVariant(period.deadline_urgency)
              : 'success';

            const templateSummary = `${period.templates.submitted} of ${templateTotal} templates submitted`;
            const deptSummary =
              period.department_reports.outstanding === 0 && deptTotal > 0
                ? 'All expected department reports approved'
                : `${period.department_reports.approved} of ${deptTotal} department reports approved`;

            const templatePreviewItems = period.templates.items.map((item) => ({
              id: `${item.department_id}-${item.id}`,
              name: item.name,
              item,
            }));
            const deptPreviewItems = period.department_reports.items.map((item) => ({
              id: item.department_id,
              name: item.department_name,
              item,
            }));

            return (
              <div
                key={period.report_tag_id}
                id={checklistPeriodAnchorId(period.report_tag_id)}
                className="dashboard-progress-period"
              >
                <button
                  type="button"
                  className="dashboard-progress-period__toggle w-100 border-0 bg-transparent text-start"
                  onClick={() =>
                    setExpandedId(isExpanded ? null : period.report_tag_id)
                  }
                  aria-expanded={isExpanded}
                >
                  <div className="dashboard-progress-period__row d-flex align-items-center justify-content-between gap-3">
                    <div className="min-w-0">
                      <div className="fw-semibold">{period.period_name}</div>
                      <small className="text-muted d-block">
                        {templateSummary}
                        {deptTotal > 0 ? ` · ${deptSummary}` : null}
                      </small>
                      {(periodLabel || deadlineLabel) && (
                        <small className="text-muted d-block">
                          {periodLabel ? `Period: ${periodLabel}` : null}
                          {periodLabel && deadlineLabel ? ' · ' : null}
                          {deadlineLabel ? `Deadline: ${deadlineLabel}` : null}
                        </small>
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                      <Badge {...subtleBadgeProps(badgeVariant)}>
                        {badgeLabel}
                      </Badge>
                      {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div
                    className="dashboard-progress-period__details px-3 pb-3 dashboard-checklist-columns"
                  >
                    <div className="dashboard-checklist-column">
                      <div className="fw-semibold text-muted small mb-2">Templates</div>
                      {templateTotal > 0 ? (
                        <>
                          <div className="small text-muted mb-1">
                            {period.templates.submitted} of {templateTotal} submitted
                          </div>
                          <ChecklistProgressBar
                            done={period.templates.submitted}
                            total={templateTotal}
                          />
                        </>
                      ) : null}
                      {period.templates.items.length === 0 ? (
                        <div className="small text-muted">No required templates for this period.</div>
                      ) : (
                        <PreviewNameList
                          items={templatePreviewItems}
                          emptyLabel="No required templates for this period."
                          limit={DASHBOARD_LAYOUT.TEMPLATE_PREVIEW_LIMIT}
                          showAllLabel={(total) => `Show all ${total}`}
                          renderItem={(row) => {
                            const item = row.item;
                            const search = buildActivityEntrySubmitSearch({
                              reportTemplateId: item.id,
                              reportTagId: period.report_tag_id,
                              departmentId: item.department_id,
                            });
                            const path = `${activitySubmitPath(scope, scopeId)}?${search.toString()}`;
                            return (
                              <li
                                className="d-flex justify-content-between align-items-start gap-2 py-1 border-bottom border-secondary-subtle"
                              >
                                <span className="min-w-0 text-break">
                                  {item.is_submitted ? '✓' : '○'} {item.name}
                                </span>
                                <span className="text-muted flex-shrink-0">
                                  {item.is_submitted ? (
                                    'Submitted'
                                  ) : canSaveActivity ? (
                                    <Link to={path} className="text-decoration-none">
                                      Open
                                    </Link>
                                  ) : (
                                    'Outstanding'
                                  )}
                                </span>
                              </li>
                            );
                          }}
                        />
                      )}
                    </div>

                    <div className="dashboard-checklist-column">
                      <div className="fw-semibold text-muted small mb-2">Department reports</div>
                      {deptTotal > 0 ? (
                        <>
                          <div className="small text-muted mb-1">
                            {period.department_reports.approved} of {deptTotal} approved
                          </div>
                          <ChecklistProgressBar
                            done={period.department_reports.approved}
                            total={deptTotal}
                          />
                        </>
                      ) : null}
                      {period.department_reports.items.length === 0 ? (
                        <div className="small text-muted">
                          No expected department reports for this period.
                        </div>
                      ) : (
                        <PreviewNameList
                          items={deptPreviewItems}
                          emptyLabel="No expected department reports for this period."
                          renderItem={(row) => {
                            const item = row.item;
                            const path = departmentProgressPath(
                              scope,
                              scopeId,
                              period.report_tag_id,
                              item.department_id
                            );
                            const readinessLabel = departmentChecklistReadinessLabel(item, scope);
                            return (
                              <li
                                className="d-flex justify-content-between align-items-start gap-2 py-1 border-bottom border-secondary-subtle"
                              >
                                <span className="min-w-0">
                                  <span className="d-block text-break">
                                    {item.is_approved ? '✓' : '○'} {item.department_name}
                                    <span className="text-muted"> · {item.status_label}</span>
                                  </span>
                                  <Badge
                                    {...subtleBadgeProps(
                                      readinessBadgeVariant(item),
                                      'dashboard-readiness-badge mt-1'
                                    )}
                                  >
                                    {readinessLabel ?? 'Complete'}
                                  </Badge>
                                </span>
                                <Link to={path} className="text-decoration-none flex-shrink-0">
                                  Open report
                                </Link>
                              </li>
                            );
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card>
  );
};

export default ReportingChecklistPanel;
